import React, { useCallback, useMemo } from "react";

import cn from "classnames";
import color from "color";
import { useIntl } from "react-intl";

import Color from "@mapmycustomers/shared/enum/Color";
import { Button } from "@mapmycustomers/ui";

import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import { TERRITORY_BANNER_CLASS_NAME } from "@app/scene/map/components/TerritoriesMode/utils/consts";
import useBoundsContext from "@app/scene/map/components/TerritoriesMode/utils/useBoundsContext";
import colors from "@app/styles/_colors.module.scss";
import anyColorToHex from "@app/util/colors/anyColorToHex";
import getContrastColor from "@app/util/colors/getContrastColor";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "../CommonBanner.module.scss";

import EditButtons from "./EditButtons";

interface Props {
  onCancel?: () => void;
}

const DrawBanner: React.FC<Props> = ({ onCancel }) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const {
    canChangeState,
    canRedo,
    canUndo,
    color: territoryColor,
    onRedo,
    onStartOver,
    onUndo,
  } = useBoundsContext();

  const [backgroundColor, textColor] = useMemo(() => {
    const background = color(anyColorToHex(territoryColor || Color.BLACK))
      .lightness(80)
      .hex();
    const textColor = getContrastColor(background) === "white" ? colors.white : colors.slate;
    return [background, textColor];
  }, [territoryColor]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    analytics.cancelled("");
  }, [analytics, onCancel]);

  return (
    <div className={cn(styles.container, TERRITORY_BANNER_CLASS_NAME)} style={{ backgroundColor }}>
      <div className={cn(styles.title, styles.createTitle)} style={{ color: textColor }}>
        <div className={styles.titles}>
          <div className={styles.mainTitle}>
            {canChangeState
              ? intl.formatMessage({
                  id: "map.territory.topPanel.createBanner.draw.beforeDrawing.title",
                  defaultMessage: "Draw new territory",
                  description: "Create Territory mode banner's title on the Map page",
                })
              : intl.formatMessage({
                  id: "map.territory.topPanel.createBanner.draw.afterDrawing.title",
                  defaultMessage: "Make changes to your territory shape",
                  description: "Create Territory mode banner's title on the Map page",
                })}
          </div>
          <div className={styles.subTitle}>
            {canChangeState
              ? intl.formatMessage({
                  id: "map.territory.topPanel.createBanner.draw.beforeDrawing.subtitle",
                  defaultMessage:
                    "Click to create the bounds of your desired shape. Double-click to close the polygon.",
                  description:
                    "Create Territory mode banner's title on the Map page (after initial shape created)",
                })
              : intl.formatMessage({
                  id: "map.territory.topPanel.createBanner.draw.afterDrawing.subtitle",
                  defaultMessage:
                    "Edit bounds by dragging them on the map. Right-click a vertex to delete.",
                  description:
                    "Create Territory mode banner's title on the Map page (after initial shape created)",
                })}
          </div>
        </div>
      </div>
      <Interactive className={styles.actions}>
        <EditButtons
          canChangeState={!canChangeState}
          canRedo={canRedo}
          canUndo={canUndo}
          onRedo={onRedo}
          onStartOver={onStartOver}
          onUndo={onUndo}
        />
        <Button onClick={handleCancel} type="default">
          {intl.formatMessage({
            id: "map.territory.topPanel.createBanner.draw.exit",
            defaultMessage: "Cancel",
            description: "Create Territory mode banner's exit button on the Map page",
          })}
        </Button>
      </Interactive>
    </div>
  );
};

export default DrawBanner;
